import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  useToast,
  HStack,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  VStack,
  Divider,
  Spacer,
  Tooltip,
  IconButton,
  useColorMode,
} from "@chakra-ui/react";
import { FaFileExcel } from "react-icons/fa";
import { ChevronDownIcon } from "@chakra-ui/icons";
import SettingsMenu from "../common/SettingsMenu";
import axiosInstance from "../../helpers/axiosInstance";
import usePersistedState from "../usePersistedState";

import { ReactComponent as DownloadIcon } from "../../assets/icons/download_2_fill_icon.svg";

const Filter = () => {
  const { colorMode } = useColorMode();
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = usePersistedState("perPageFilter", 8); // Default to 10 messages per page
  const [totalPages, setTotalPages] = useState(0);
  const [usersMinDate, setUsersMinDate] = useState("");
  const [usersMaxDate, setUsersMaxDate] = useState("");
  const [usersMinLogin, setUsersMinLogin] = useState("");
  const [usersMaxLogin, setUsersMaxLogin] = useState("");
  const [usersMinLatestLogin, setUsersMinLatestLogin] = useState("");
  const [usersMaxLatestLogin, setUsersMaxLatestLogin] = useState("");
  const [registrationDevices, setRegistrationDevices] = useState([]);
  const [loginDevices, setLoginDevices] = useState([]);
  const [excelFile, setExcelFile] = useState("");
  const [filters, setFilters] = useState({
    minDate: usersMinDate,
    maxDate: usersMaxDate,
    deviceType: [],
    logindeviceType: [],
    minLogins: usersMinLogin,
    maxLogins: usersMaxLogin,
    email: "",
    minLatestLoginDate: usersMinLatestLogin,
    maxLatestLoginDate: usersMaxLatestLogin,
    sortBy: "",
    sortOrder: "desc",
  });
  const toast = useToast();

  const handleDeviceTypeChange = (device) => {
    setFilters((prevFilters) => {
      const currentDevices = prevFilters.deviceType;
      const nextFilters = currentDevices.includes(device)
        ? {
            ...prevFilters,
            deviceType: currentDevices.filter((d) => d !== device),
          }
        : { ...prevFilters, deviceType: [...currentDevices, device] };
      // Trigger fetch with updated filters
      fetchUsers(nextFilters);
      return nextFilters;
    });
  };

  const isDeviceSelected = (device) => {
    return filters.deviceType.includes(device);
  };

  const handleLoginDeviceTypeChange = (device) => {
    setFilters((prevFilters) => {
      const currentDevices = prevFilters.logindeviceType;
      const nextFilters = currentDevices.includes(device)
        ? {
            ...prevFilters,
            logindeviceType: currentDevices.filter((d) => d !== device),
          }
        : { ...prevFilters, logindeviceType: [...currentDevices, device] };
      // Trigger fetch with updated filters
      fetchUsers(nextFilters);
      return nextFilters;
    });
  };

  const isLoginDeviceSelected = (device) => {
    return filters.logindeviceType.includes(device);
  };

  // Function to fetch users based on provided or current filters and pagination
  const fetchUsers = (overrideFilters = null) => {
    const effectiveFilters = overrideFilters ? overrideFilters : filters;
    const params = {
      page: currentPage,
      per_page: perPage,
      ...effectiveFilters,
    };
    // console.log("payload", params);
    // Validate registration date range
    if (
      effectiveFilters.minDate &&
      effectiveFilters.maxDate &&
      new Date(effectiveFilters.minDate) > new Date(effectiveFilters.maxDate)
    ) {
      toast({
        title: "Registration Date Range Error",
        description:
          "The minimum registration date cannot be later than the maximum registration date.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validate latest login date range
    if (
      effectiveFilters.minLatestLoginDate &&
      effectiveFilters.maxLatestLoginDate &&
      new Date(effectiveFilters.minLatestLoginDate) >
        new Date(effectiveFilters.maxLatestLoginDate)
    ) {
      toast({
        title: "Latest Login Date Range Error",
        description:
          "The minimum latest login date cannot be later than the maximum latest login date.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validate login count range
    if (
      effectiveFilters.minLogins &&
      effectiveFilters.maxLogins &&
      parseInt(effectiveFilters.minLogins) >
        parseInt(effectiveFilters.maxLogins)
    ) {
      toast({
        title: "Login Count Range Error",
        description:
          "The minimum login count cannot be greater than the maximum login count.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    //console.log("payload", params);
    axiosInstance
      .post(`/filter/get_users`, params)
      .then((response) => {
        const data = response.data.users_info;
        // console.log("Response", data);

        setUsers(data.users);
        setTotalPages(data.total_pages);
        setUsersMinDate(data.min_registration_date);
        setUsersMaxDate(data.max_registration_date);
        setUsersMinLogin(data.min_login_count);
        setUsersMaxLogin(data.max_login_count);
        setFilters((prev) => ({
          ...prev,
          minDate: prev.minDate || data.min_registration_date,
          maxDate: prev.maxDate || data.max_registration_date,
          minLogins: prev.minLogins || data.min_login_count,
          maxLogins: prev.maxLogins || data.max_login_count,
          minLatestLoginDate: prev.minLatestLoginDate || "",
          maxLatestLoginDate: prev.maxLatestLoginDate || "",
        }));
        setExcelFile(data.excel_file);
        if (currentPage > totalPages) {
          setCurrentPage(Math.max(totalPages, 1));
        }
        // Extract unique devices
        const uniqueRegistrationDevices = [
          ...new Set(data.users.map((user) => user.registration_device_type)),
        ];
        const uniqueLoginDevices = [
          ...new Set(data.users.map((user) => user.login_device_type)),
        ];
        setRegistrationDevices(uniqueRegistrationDevices);
        setLoginDevices(uniqueLoginDevices);
      })
      .catch((error) => {});
  };

  const handleDownloadClick = () => {
    if (!excelFile) {
      toast({
        title: "Error",
        description: "No file to download.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${excelFile}`;
    link.download = "users.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (perPage > 0) {
      fetchUsers();
    }
  }, [currentPage, perPage]);

  // Handlers for pagination
  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handler for filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nextFilters = {
      ...filters,
      [name]: value === "" ? null : value,
    };
    setFilters(nextFilters);
    fetchUsers(nextFilters);
  };

  const [filtersCleared, setFiltersCleared] = useState(false);

  useEffect(() => {
    if (filtersCleared) {
      // You may need to add a new state to track if filters are cleared
      fetchUsers();
      setFiltersCleared(false); // Reset the flag
    }
  }, [filters]); // Runs fetchUsers() whenever filters state changes

  const clearFilters = () => {
    setFilters({
      minDate: usersMinDate,
      maxDate: usersMaxDate,
      deviceType: [],
      logindeviceType: [],
      minLogins: usersMinLogin,
      maxLogins: usersMaxLogin,
      email: "",
      minLatestLoginDate: usersMinLatestLogin,
      maxLatestLoginDate: usersMaxLatestLogin,
      sortBy: "",
      sortOrder: "desc",
    });
    setFiltersCleared(true); // Set a flag that filters are cleared
  };

  return (
    <Box h="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Box
        flexShrink={0}
        display="flex"
        flexDirection="column"
        maxH="8vh"
        borderBottom="1px"
        borderColor="blackAlpha.200"
        overflowY="hidden"
      >
        <Box
          display="flex"
          alignItems="center"
          bg={colorMode === "dark" ? "gray.700" : "white"}
          width="100%"
          height="100%"
          p={4}
          pt={2}
          pb={2}
          boxSizing="border-box"
          overflowY="hidden"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mb={0}
            mt={0}
            color={colorMode === "dark" ? "gray.200" : "blackAlpha.700"}
          >
            Users
          </Text>
          <Spacer />

          <SettingsMenu />
        </Box>
      </Box>
      {/*
      <Flex
        bg="white"
        // h={{ base: "40%", md: "27%" }}
        h="auto"
        justifyContent="center"
        alignItems="center"
        p={4}
        w="100%"
      >
        <Box bg="white" m={0} p={4} borderRadius="lg" shadow="lg">
          <HStack justifyContent="flex-start" spacing={2} wrap="wrap">
            ...
          </HStack>
        </Box>
      </Flex>
      */}
      {/* <Divider m={0} /> */}
      <Box p={4} flex="1" overflowY="auto">
        {/* <Divider m={0} /> */}
        <Table variant="simple" border="1px" borderColor="gray.200" size="sm">
          <Thead>
            <Tr>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Username
                  </Text>
                </HStack>
              </Th>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Email
                  </Text>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="outline"
                      icon={<ChevronDownIcon />}
                    />
                    <MenuList>
                      <Box p={2}>
                        <Input
                          size="sm"
                          placeholder="Search by email..."
                          name="email"
                          value={filters.email || ""}
                          onChange={handleFilterChange}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                    </MenuList>
                  </Menu>
                </HStack>
              </Th>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Registration Date
                  </Text>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="outline"
                      icon={<ChevronDownIcon />}
                    />
                    <MenuList>
                      <MenuItem
                        onClick={() => {
                          const next = {
                            ...filters,
                            sortBy: "registration_date",
                            sortOrder: "desc",
                          };
                          setFilters(next);
                          fetchUsers(next);
                        }}
                      >
                        Newest First
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          const next = {
                            ...filters,
                            sortBy: "registration_date",
                            sortOrder: "asc",
                          };
                          setFilters(next);
                          fetchUsers(next);
                        }}
                      >
                        Oldest First
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </Th>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Register Device
                  </Text>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="outline"
                      icon={<ChevronDownIcon />}
                    />
                    <MenuList>
                      {registrationDevices.map((device, index) => (
                        <MenuItem
                          key={index}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            isChecked={isDeviceSelected(device)}
                            onChange={() => handleDeviceTypeChange(device)}
                          >
                            {device}
                          </Checkbox>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </HStack>
              </Th>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Login Device
                  </Text>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="outline"
                      icon={<ChevronDownIcon />}
                    />
                    <MenuList>
                      {loginDevices.map((device, index) => (
                        <MenuItem
                          key={index}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            isChecked={isLoginDeviceSelected(device)}
                            onChange={() => handleLoginDeviceTypeChange(device)}
                          >
                            {device}
                          </Checkbox>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </HStack>
              </Th>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Login Count
                  </Text>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="outline"
                      icon={<ChevronDownIcon />}
                    />
                    <MenuList>
                      <Box p={2}>
                        <HStack align="start" spacing={3}>
                          <FormControl>
                            <FormLabel mb={1} fontSize="xs">Min</FormLabel>
                            <Input
                              size="sm"
                              placeholder="Min"
                              name="minLogins"
                              value={filters.minLogins || ""}
                              onChange={handleFilterChange}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel mb={1} fontSize="xs">Max</FormLabel>
                            <Input
                              size="sm"
                              placeholder="Max"
                              name="maxLogins"
                              value={filters.maxLogins || ""}
                              onChange={handleFilterChange}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </FormControl>
                        </HStack>
                      </Box>
                    </MenuList>
                  </Menu>
                </HStack>
              </Th>
              <Th>
                <HStack spacing={2} justifyContent="left">
                  <Text fontSize="sm" fontWeight="bold" mb={0}>
                    Latest Login Date
                  </Text>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="outline"
                      icon={<ChevronDownIcon />}
                    />
                    <MenuList>
                      <MenuItem
                        onClick={() => {
                          const next = {
                            ...filters,
                            sortBy: "latest_login_date",
                            sortOrder: "desc",
                          };
                          setFilters(next);
                          fetchUsers(next);
                        }}
                      >
                        Newest First
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          const next = {
                            ...filters,
                            sortBy: "latest_login_date",
                            sortOrder: "asc",
                          };
                          setFilters(next);
                          fetchUsers(next);
                        }}
                      >
                        Oldest First
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user, index) => (
              <Tr key={index}>
                <Td>{user.username}</Td>
                <Td>{user.email}</Td>
                <Td>{user.registration_date}</Td>
                <Td>{user.registration_device_type}</Td>
                <Td>{user.login_device_type}</Td>
                <Td>{user.login_count}</Td>
                <Td>{user.latest_login_date}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Box mt={1} mb={1} ml={4} display="flex" justifyContent="left" flexShrink={0} borderTop="1px" borderColor="gray.200">
        <HStack spacing={4} align="center">
          <Button
            size="sm"
            onClick={handlePreviousClick}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>
          <Text pt={3} size="sm">
            {currentPage} of {totalPages}
          </Text>
          <Button
            size="sm"
            onClick={handleNextClick}
            disabled={currentPage >= totalPages}
          >
            Next
          </Button>
          <Box>
            <FormControl>
              <HStack>
                <IconButton
                  aria-label="Download Excel"
                  icon={<Box as={DownloadIcon} boxSize="5" />}
                  color="green"
                  bg="none"
                  _hover="none"
                  size="lg"
                  onClick={handleDownloadClick}
                />
              </HStack>
            </FormControl>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export default Filter;
